import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { KeyRound, LogOut, Mail, ShieldAlert, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { errText } from '../../account/ChangePasswordCard';
import { fmtWhen } from '../../account/MyAccountTab';

const API = process.env.REACT_APP_BACKEND_URL;
const LABELS = {
  password_changed: 'Password changed', password_change_failed: 'Wrong current password entered', reset_requested: 'Reset link requested',
  reset_completed: 'Password reset completed', forced_reset_enabled: 'Forced password reset', sessions_revoked: 'Signed out from all devices', mfa_enabled: 'Authenticator turned on', mfa_disabled: 'Authenticator turned off',
  mfa_reset: 'Authenticator reset', mfa_failed: 'Too many wrong authenticator codes', mfa_backup_code_used: 'Signed in with a backup code',
  mfa_backup_codes_regenerated: 'New backup codes created', invite_link_sent: 'Invite link sent', invite_password_set: 'Password set from invite',
};
const RESET_MFA = { key: 'reset-mfa', label: 'Reset Authenticator (lost phone)', confirm: (u) => `Remove ${u.email}'s authenticator and backup codes and sign them out? They can set it up again after signing in. (Super Admin only)` };
const ACTIONS = [
  { key: 'send-reset-link', label: 'Send Password Reset Link', icon: Mail, confirm: (u) => `Email a one-time reset link (valid 30 minutes) to ${u.email}?` },
  { key: 'force-reset', label: 'Force Password Reset', icon: ShieldAlert, confirm: (u) => `Sign ${u.email} out everywhere and require a new password? A reset link will be emailed.` },
  { key: 'revoke-sessions', label: 'Sign Out From All Devices', icon: LogOut, confirm: (u) => `Sign ${u.email} out of every device now?` },
];

// Admin → User Management → Security: last login / password change, reset & sign-out actions, recent security events.
export const UserSecurityDialog = ({ user, token, onClose }) => {
  const [sec, setSec] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(() => {
    axios.get(`${API}/api/admin/users/${user.id}/security`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setSec(r.data); setError(''); }).catch((e) => setError(errText(e, 'Could not load security details')));
  }, [user.id, token]);
  useEffect(() => { load(); }, [load]);

  const run = async (a) => {
    if (!window.confirm(a.confirm(user))) return;
    setBusy(a.key);
    try {
      const { data } = await axios.post(`${API}/api/admin/users/${user.id}/security/${a.key}`, {}, { headers });
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(errText(e, 'Action failed'));
    } finally {
      setBusy('');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-security-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-amber-600" /> Account Security</DialogTitle>
          <DialogDescription>{user.name} · {user.email}</DialogDescription>
        </DialogHeader>
        {error ? <p className="text-sm text-red-600" data-testid="user-security-error">{error}</p> : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Last login</p><p className="font-medium" data-testid="user-security-last-login">{sec ? fmtWhen(sec.last_login_at) : '…'}</p></div>
              <div className="rounded-lg bg-neutral-50 p-3"><p className="text-xs text-neutral-500">Last password change</p><p className="font-medium" data-testid="user-security-last-password-change">{sec ? (sec.password_changed_at ? fmtWhen(sec.password_changed_at) : 'Not changed yet') : '…'}</p></div>
            </div>
            {sec?.must_reset_password && <p className="text-xs rounded-md bg-amber-50 text-amber-800 px-3 py-2" data-testid="user-security-force-pending">Waiting for this user to set a new password.</p>}
            <div className="grid gap-2">
              {ACTIONS.map((a) => (
                <Button key={a.key} variant="outline" className="justify-start" disabled={!!busy} onClick={() => run(a)} data-testid={`user-security-${a.key}-btn`}>
                  <a.icon className="h-4 w-4 mr-2" />{busy === a.key ? 'Working…' : a.label}
                </Button>
              ))}
              {sec?.two_factor_enabled && (
                <Button variant="outline" className="justify-start text-red-700" disabled={!!busy} onClick={() => run(RESET_MFA)} data-testid="user-security-reset-mfa-btn">
                  <Smartphone className="h-4 w-4 mr-2" />{busy === RESET_MFA.key ? 'Working…' : RESET_MFA.label}
                </Button>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">Recent security activity</p>
              <ul className="space-y-1.5 text-xs max-h-40 overflow-y-auto" data-testid="user-security-events">
                {(sec?.recent_events || []).length === 0 && <li className="text-neutral-400">No security events yet.</li>}
                {(sec?.recent_events || []).map((ev) => (
                  <li key={ev.id} className="flex justify-between gap-3">
                    <span>{LABELS[ev.action] || ev.action}{ev.actor_email && ev.actor_email !== user.email ? ` · by ${ev.actor_email}` : ''}</span>
                    <span className="text-neutral-500 shrink-0">{fmtWhen(ev.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-xs text-neutral-400">Passwords are never shown. Users set their own password through a secure one-time link.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
